<template>
  <b-modal
    id="EditRdmScopeDialog"
    ref="EditRdmScopeDialog"
    title="แก้ไขขอบเขต RDM (สาขาที่ดูแล)"
    size="lg"
    centered
    no-close-on-backdrop
    @hide="onHide"
    @ok.prevent="submit"
  >
    <div v-if="targetUser" class="target-strip mb-3">
      <div class="text-muted small">ผู้จัดการเขต (RDM)</div>
      <div class="font-weight-bold">
        {{ targetUser.firstNameTh }} {{ targetUser.lastNameTh }}
      </div>
    </div>

    <div class="replace-warn mb-3">
      <i class="fas fa-exclamation-triangle mr-2"></i>
      <span>
        รายการที่เลือกจะ <strong>แทนที่</strong> ขอบเขตปัจจุบันทั้งหมด —
        ไม่ใช่การเพิ่มเติม
      </span>
    </div>

    <b-form-group label="สาขาที่ดูแล" label-class="font-weight-bold">
      <div v-if="branches.length === 0" class="text-muted small">
        ยังไม่มีรายการสาขา
      </div>
      <div v-else class="branch-list border rounded p-2">
        <div class="mb-2 pb-2 border-bottom">
          <b-form-checkbox v-model="selectAll" @change="onToggleAll">
            เลือกทั้งหมด ({{ branches.length }})
          </b-form-checkbox>
        </div>
        <b-form-checkbox-group
          v-model="selectedBranchIds"
          stacked
          :options="branchOptions"
        />
      </div>
    </b-form-group>

    <!-- Live diff vs the current scope -->
    <div class="diff-summary mb-3">
      <div class="diff-col added">
        <div class="diff-col-head">
          <i class="fas fa-plus-circle mr-1"></i>เพิ่ม ({{ addedBranches.length }})
        </div>
        <div v-if="addedBranches.length" class="diff-chips">
          <b-badge v-for="b in addedBranches" :key="b.id" variant="success" class="mr-1 mb-1">
            {{ b.name }}
          </b-badge>
        </div>
        <div v-else class="text-muted small">—</div>
      </div>
      <div class="diff-col removed">
        <div class="diff-col-head">
          <i class="fas fa-minus-circle mr-1"></i>ถอด ({{ removedBranches.length }})
        </div>
        <div v-if="removedBranches.length" class="diff-chips">
          <b-badge v-for="b in removedBranches" :key="b.id" variant="danger" class="mr-1 mb-1">
            {{ b.name }}
          </b-badge>
        </div>
        <div v-else class="text-muted small">—</div>
      </div>
      <div class="diff-count">
        ปัจจุบัน {{ originalBranchIds.length }} → ใหม่ {{ selectedBranchIds.length }} สาขา
      </div>
    </div>

    <b-form-group
      label="เหตุผล (อย่างน้อย 10 ตัวอักษร)"
      label-class="font-weight-bold"
      :invalid-feedback="reasonError"
      :state="reasonState"
    >
      <b-form-textarea v-model="reason" rows="3" :state="reasonState" />
    </b-form-group>

    <b-alert v-if="errorMessage" show variant="danger" class="mt-2 mb-0">
      {{ errorMessage }}
    </b-alert>

    <template #modal-footer="{ ok, cancel }">
      <b-button variant="secondary" :disabled="isSubmitting" @click="cancel">
        ยกเลิก
      </b-button>
      <b-button
        variant="primary"
        :disabled="!canSubmit || isSubmitting"
        @click="ok"
      >
        <b-spinner v-if="isSubmitting" small class="mr-1" />
        บันทึกขอบเขต ({{ selectedBranchIds.length }})
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import axios from "axios";

export default {
  name: "EditRdmScopeDialog",
  data() {
    return {
      targetUser: null,
      clinicUrl: null,
      branches: [],
      selectedBranchIds: [],
      // Snapshot of the scope when the dialog opened, for the diff summary.
      originalBranchIds: [],
      reason: "",
      isSubmitting: false,
      errorMessage: "",
    };
  },
  computed: {
    branchOptions() {
      return this.branches.map(b => ({ value: b.id, text: b.name }));
    },
    selectAll: {
      get() {
        return (
          this.branches.length > 0 &&
          this.selectedBranchIds.length === this.branches.length
        );
      },
      set() {
        // Handled via @change to access the toggled value.
      },
    },
    // Branches newly added vs the original scope.
    addedBranches() {
      const orig = new Set(this.originalBranchIds);
      return this.branches.filter(
        b => this.selectedBranchIds.includes(b.id) && !orig.has(b.id)
      );
    },
    // Branches present originally but now unselected.
    removedBranches() {
      const sel = new Set(this.selectedBranchIds);
      return this.branches.filter(
        b => this.originalBranchIds.includes(b.id) && !sel.has(b.id)
      );
    },
    reasonState() {
      if (this.reason.length === 0) return null;
      return this.reason.trim().length >= 10;
    },
    reasonError() {
      if (this.reason.length > 0 && this.reason.trim().length < 10) {
        return "กรุณาระบุเหตุผลอย่างน้อย 10 ตัวอักษร";
      }
      return "";
    },
    canSubmit() {
      // Allow empty selection (= clear scope) only if reason is provided.
      return this.reason.trim().length >= 10;
    },
  },
  methods: {
    show(payload) {
      // payload: { targetUser, clinicUrl, branches, currentBranchIds }
      this.targetUser = payload.targetUser;
      this.clinicUrl = payload.clinicUrl;
      this.branches = payload.branches || [];
      const current = Array.isArray(payload.currentBranchIds)
        ? [...payload.currentBranchIds]
        : [];
      this.selectedBranchIds = [...current];
      this.originalBranchIds = [...current];
      this.reason = "";
      this.errorMessage = "";
      this.isSubmitting = false;
      this.$bvModal.show("EditRdmScopeDialog");
    },
    close() {
      this.$bvModal.hide("EditRdmScopeDialog");
    },
    onHide() {
      this.errorMessage = "";
      this.isSubmitting = false;
    },
    onToggleAll(checked) {
      this.selectedBranchIds = checked ? this.branches.map(b => b.id) : [];
    },
    async submit() {
      if (!this.canSubmit || this.isSubmitting) return;
      this.errorMessage = "";
      this.isSubmitting = true;
      try {
        await axios.post(
          `/api/userMgmt/${this.clinicUrl}/central-members/${this.targetUser.uid}/branches`,
          {
            branchIds: this.selectedBranchIds,
            reason: this.reason.trim(),
          },
          { skipGlobalErrorHandler: true }
        );
        this.$emit("scope-updated", {
          uid: this.targetUser.uid,
          branchIds: [...this.selectedBranchIds],
        });
        this.close();
      } catch (err) {
        this.errorMessage =
          err.response?.data?.errorMessage?.message ||
          err.response?.data?.message ||
          err.message ||
          "ไม่สามารถบันทึกขอบเขตได้";
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.target-strip {
  background: #f6f8fb;
  border: 1px solid #e3e8ef;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
}

.replace-warn {
  display: flex;
  align-items: flex-start;
  background: #fff8df;
  border: 1px solid #f0d98a;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
}

.replace-warn .fas {
  color: #b8860b;
  margin-top: 0.15rem;
}

.branch-list {
  max-height: 240px;
  overflow-y: auto;
  background-color: #fafafa;
}

.diff-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem;
  align-items: stretch;
}

.diff-col {
  flex: 1 1 0;
  min-width: 140px;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.5rem 0.625rem;
}

.diff-col.added {
  background: #eafaf0;
  border-color: #bfe6cc;
}

.diff-col.removed {
  background: #fdecea;
  border-color: #f3c0bb;
}

.diff-col-head {
  font-size: 0.8125rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.diff-count {
  flex: 1 1 100%;
  text-align: right;
  font-size: 0.8125rem;
  color: #6b7280;
}
</style>
